import { type Database } from '@databases/model/database';
import { type Entry } from '@databases/model/entry';
import { type Field, FieldType } from '@databases/model/field';
import {
  type UserValue,
  type SelectValue,
  type Value,
  UserType,
  type TextValue,
  type NumberValue,
  type DateValue,
  type PageLinkValue,
  type HyperlinkValue,
  type JiraIssueValue,
} from '@databases/model/value';
import { unparse } from 'papaparse';

import { type Warnings } from './warnings';

export type SelectedCells = Set<string>;

export function getCellKey(entryId: string, fieldId: string): string {
  return `${entryId}:${fieldId}`;
}

export function createSelectedCells(cells: Array<[string, string]>): SelectedCells {
  return new Set(cells.map(([entryId, fieldId]) => getCellKey(entryId, fieldId)));
}

export function encodeDatabaseValue(
  value: Value<FieldType>,
  field: Field
): string | undefined {
  switch (value.type) {
    case FieldType.Text:
      return (value as TextValue).value;
    case FieldType.Number:
      return (value as NumberValue).value.toString();
    case FieldType.Date:
      return (value as DateValue).value;
    case FieldType.Select:
      return (value as SelectValue).value
        .map(
          (value) =>
            field.selectOptions.options.find((option) => option.id === value)?.label
        )
        .filter(Boolean)
        .join('|');
    case FieldType.User:
      return (value as UserValue).value
        .map((user) =>
          user.type === UserType.Referenced ? user.accountId : 'Current User'
        )
        .join('|');
    case FieldType.PageLink:
      return (value as PageLinkValue).value
        .map((link) => link.ari || link.contentId || '')
        .filter(Boolean)
        .join('|');
    case FieldType.Hyperlink:
      return (value as HyperlinkValue).value.map((link) => link.url).join('|');
    case FieldType.JiraIssue:
      return (value as JiraIssueValue).value.map((issue) => issue.key).join('|');
    default:
      return undefined;
  }
}

function encodeDatabaseRow(entry: Entry, database: Database, index: number) {
  const row: Record<string, string> = {
    _id: String(index),
  };
  for (const fieldId of database.fields) {
    const value = entry.getValue(fieldId);
    const field = database.fieldsById[fieldId];

    if (value) {
      row[field.name] = encodeDatabaseValue(value, field) ?? '';
    }
  }
  return row;
}

/**
 * Serializes the contents of a database.
 *
 * This will serialize as a CSV with the following format:
 *
 * _id,Field 1,Field 2,Field 3
 * 1,Value 1,Value 2,Value 3
 * 2,Value 4,Value 5,Value 6
 *
 * An `_id` field is added to the beginning of the row to identify the entry, which uses a 1-based index.
 * Multi-value fields (select, user, etc.) use pipe delimiters (e.g., Tag1 or Tag1|Tag2).
 *
 * @param database The database to serialize the contents of.
 * @returns The serialized contents.
 */
export function serializeDatabaseContents(
  database: Database,
  _warnings: Warnings,
  rowIdToEntryIdMappings: Map<string, string>
): string {
  const rows = database.entries
    .map((entryId, index) => {
      const entry = database.entriesById[entryId];

      if (entry) {
        const row = encodeDatabaseRow(entry, database, index + 1);
        rowIdToEntryIdMappings.set(String(index + 1), entryId);
        return row;
      }

      return undefined;
    })
    .filter(Boolean);
  const columns = Object.values(database.fieldsById).map((field) => field.name);

  return unparse(rows, {
    header: true,
    escapeChar: '\\',
    columns: ['_id', ...columns],
  });
}

/**
 * Serializes the selected cells as a separate table.
 *
 * This will serialize as a CSV with the following format:
 *
 * selected_entry_id,selected_field
 * 1,Text
 * 2,Number
 *
 * @param selectedCells Set of selected cell keys in the format "entryId:fieldId"
 * @param database The database to get field names and entry IDs from
 * @param rowIdToEntryIdMappings Mapping from row IDs to entry IDs
 * @returns The serialized selected cells table.
 */
export function serializeSelectedCells(
  selectedCells: SelectedCells,
  database: Database,
  rowIdToEntryIdMappings: Map<string, string>
): string {
  const entryIdToRowId = new Map(
    Array.from(rowIdToEntryIdMappings.entries()).map(([rowId, entryId]) => [
      entryId,
      rowId,
    ])
  );

  const rows = Array.from(selectedCells)
    .map((cellKey) => {
      const [entryId, fieldId] = cellKey.split(':');
      const rowId = entryIdToRowId.get(entryId);
      const field = database.fieldsById[fieldId];

      if (!rowId || !field) {
        return undefined;
      }

      return {
        selected_entry_id: rowId,
        selected_field: field.name,
      };
    })
    .filter(Boolean);

  if (rows.length === 0) {
    return 'selected_entry_id,selected_field\r\n';
  }

  return unparse(rows, {
    header: true,
    escapeChar: '\\',
    columns: ['selected_entry_id', 'selected_field'],
  });
}
